import { forwardRef } from 'react';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Stack from '@mui/material/Stack';
import TableNameCell from './TableNameCell';
import GenericTableCell from '../GenericTableCell';
import SecurityActions from '../DownloadActions';
import { isDateString } from '../../utils/date.util';
import { formatDate } from '../../utils/formatTime';
import { ROW_SOURCES } from '../../constants/table';
import Button from '../Button';
import { Box, Chip, Tooltip } from '@mui/material';
import Iconify from '../Iconify';
import {
  CATALOG_GENERATION_STATUS,
  getCatalogGenerationStatusColor,
} from '../../utils/enums/catalogGenerationStatus';
import {
  getCustomerEnvironmentApplicationStatusLabel,
  getCustomerEnvironmentApplicationStatusValue,
} from 'src/utils/enums/customerEnvironmentApplicationStatus';
import { SOURCE_BADGE_META } from 'src/pages/apps/Table';

// The status arrives as a string and callers have not always agreed on its casing.
const isCatalogStatus = (value, status) =>
  String(value || '').toLowerCase() === status.toLowerCase();

// A failed generation is called out because it otherwise looks like one still running.
const CatalogDownloadCell = ({ catalogGenerationStatus, policies, versionId }) => {
  if (isCatalogStatus(catalogGenerationStatus, CATALOG_GENERATION_STATUS.Failed)) {
    return (
      <Tooltip title="Catalog generation failed for this version, so there is nothing to download.">
        <Box component="span" display="inline-flex">
          <Iconify
            icon="mdi:alert-circle-outline"
            color={getCatalogGenerationStatusColor(CATALOG_GENERATION_STATUS.Failed)}
            width={24}
            height={24}
          />
        </Box>
      </Tooltip>
    );
  }

  if (isCatalogStatus(catalogGenerationStatus, CATALOG_GENERATION_STATUS.Ready)) {
    return <SecurityActions policies={policies} versionId={versionId} />;
  }

  return null;
};

/**
 *
 * @typedef TableExpandedRowProps
 * @property {Object} row
 */

/**
 * TableExpandedRow components
 *
 * @param {TableExpandedRowProps} props - props of component
 */

const TableExpandedRow = forwardRef(
  ({ onClick, data, columnsOrder, source, expandedRowProps }, ref) => {
    const renderCell = (column, item) => {
      if (column?.name === 'status' || column === 'status') {
        if (source !== ROW_SOURCES.apps || !expandedRowProps) {
          return <TableCell key="status" />;
        }

        const {
          applicationStatus,
          applicationNotes,
          onStatusClick,
          applicationStatusUpdatedOn,
          applicationId,
        } = expandedRowProps;

        const versionStatus = getCustomerEnvironmentApplicationStatusValue(
          item.status ?? applicationStatus,
        );
        const statusLabel = getCustomerEnvironmentApplicationStatusLabel(versionStatus);
        const buttonLabel = statusLabel;
        const notesValue = item.statusNotes ?? item.notes ?? applicationNotes;
        const updatedOnValue = item.statusUpdatedOn ?? applicationStatusUpdatedOn;
        const targetApplicationId =
          item.customerEnvironmentApplicationId ?? item.id ?? applicationId;

        const handleStatusClick = () => {
          if (!onStatusClick) return;
          onStatusClick({
            applicationId: targetApplicationId,
            status: versionStatus,
            notes: notesValue,
            date: updatedOnValue,
          });
        };

        return (
          <TableCell key="status" onClick={e => e.stopPropagation()}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {notesValue ? (
                <Tooltip
                  placement="top"
                  title={notesValue}
                  componentsProps={{
                    tooltip: {
                      sx: { bgcolor: '#3F3F3F' },
                    },
                  }}>
                  <Button
                    variant={statusLabel}
                    size="small"
                    sx={{
                      fontSize: '12px',
                      padding: '4px',
                      ml: '28px',
                    }}
                    onClick={handleStatusClick}>
                    {buttonLabel}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant={statusLabel}
                  size="small"
                  sx={{
                    fontSize: '12px',
                    padding: '4px',
                    ml: '28px',
                  }}
                  onClick={handleStatusClick}>
                  {buttonLabel}
                </Button>
              )}
            </Stack>
          </TableCell>
        );
      }

      const columnName = column?.name || column;

      if (columnName === '') {
        return <TableCell key="empty" />;
      }

      if (columnName === 'versionsCount') {
        return <TableCell key="versionsCount-empty" />;
      }

      if (columnName === 'source') {
        const sourceValue = item.source || source || 'Unknown';

        const normalized = sourceValue.toLowerCase().replace(/_/g, ' ');

        let badge = SOURCE_BADGE_META[normalized] || {
          label: sourceValue
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' '),
          color: '#DDDDDD',
          borderColor: 'rgba(221, 221, 221, 0.3)',
          backgroundColor: 'transparent',
        };

        return (
          <TableCell key="source">
            <Chip
              size="small"
              label={badge.label}
              sx={{
                height: 22,
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'none',
                color: badge.color,
                borderColor: badge.borderColor,
                backgroundColor: badge.backgroundColor,
              }}
              variant="outlined"
            />
          </TableCell>
        );
      }

      const cellMaps = {
        environmentApplicationName: (
          <TableNameCell
            key={item.environmentApplicationName}
            name={item.environmentApplicationName}
            imgSrc={item.icon}
          />
        ),
      };
      const defaultCell = (
        <GenericTableCell
          key={`${columnName}-${item.id}`}
          value={isDateString(item[columnName]) ? formatDate(item[columnName]) : item[columnName]}
          styles={
            columnName === 'createdAt'
              ? {
                  paddingRight: 6,
                  minWidth: '120px',
                }
              : undefined
          }
        />
      );

      return cellMaps[columnName] || defaultCell;
    };
    return (
      <>
        {data.map((version, index) => (
          <TableRow
            onClick={() => onClick(version.id)}
            ref={index === data.length - 1 ? ref : null}
            key={version.id}
            sx={{
              backgroundColor: '#393939',
              cursor: 'pointer',
            }}>
            {columnsOrder.map(column => renderCell(column, version, index))}
            <TableCell
              align="center"
              sx={{ verticalAlign: 'middle', paddingY: '12px' }}
              onClick={e => e.stopPropagation()}>
              {source === ROW_SOURCES.files ? null : (
                <CatalogDownloadCell
                  catalogGenerationStatus={version.catalogGenerationStatus}
                  policies={version.policies}
                  versionId={version.id}
                />
              )}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  },
);

export default TableExpandedRow;
